/* eslint-disable react/display-name */
import { priceFormatter } from '@2wunder/klarx-tool'
import { ColumnProps } from 'antd/lib/table'
import Actions from 'app/components/Core/Rental/Actions'
import KTable from 'app/components/Modules/Table/_KTable'
import Button from 'app/components/UI-Elements/General/Button'
import { RecordType, TableProps } from 'app/constants/GlobalTypes'
import { convertIsoToString } from 'app/lib/apollo/normalizers/utilities'
import { ToolBox } from 'app/lib/hooks/toolBox'
import useTranslation from 'app/lib/hooks/useTranslation'
import { RentalFilters } from 'app/pages/Rental/Views'
import { RecordTypeEnum } from 'app/queries'
import { Routes } from 'app/routes/Routes'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'

import RentalEmpty from './Empty'
import MachinesCell from './MachinesCell'
import Item from './Mobile/Item'
import RentalStatusTag from './StatusTag'

const TableAction = ({ rental }: { rental: IRental }) => {
  const { tButtonTitle } = ToolBox.useString()

  return (
    <Choose>
      <When condition={rental.status_info.actions[0]}>
        <Actions props={{ size: 'small' }} member={rental} />
      </When>
      <Otherwise>
        <Link to={Routes.Rental.member(rental.id.toString()).show}>
          <Button
            size="small"
            title={tButtonTitle({
              recordType: RecordType.rental,
              action: 'link'
            })}
            type="default"
          />
        </Link>
      </Otherwise>
    </Choose>
  )
}

type Props = {
  hasRentals?: boolean
  setFilter?: (filters: RentalFilters) => void
}

const RentalTable: React.FC<TableProps<IRental> & Props> = ({
  params,
  hasRentals,
  setFilter,
  ...others
}) => {
  const { formatMessage } = useIntl()
  const { tCore } = useTranslation({ recordType: RecordType.rental, scope: 'empty' })

  const columns: ColumnProps<IRental>[] = [
    {
      title: formatMessage({ id: 'rental.index.table.header.name' }),
      dataIndex: 'project_name',
      fixed: 'left',
      width: 240,
      render: (_, rental) => <strong>{rental.project?.name || '-'}</strong>
    },
    {
      title: formatMessage({ id: 'rental.index.table.header.status' }),
      dataIndex: 'status',
      width: 165,
      render: (_, rental) => <RentalStatusTag member={rental} />
    },
    {
      title: formatMessage({ id: 'rental.index.table.header.machines' }),
      dataIndex: 'machine_names',
      render: (_, rental) => <MachinesCell member={rental} />,
      ellipsis: true,
      width: 150
    },
    {
      title: formatMessage({ id: 'rental.index.table.header.duration' }),
      dataIndex: 'period',
      width: 160,
      render: (_, rental) => (
        <span>{`${convertIsoToString(rental.startDate)} - ${convertIsoToString(
          rental.returnDate
        )}`}</span>
      )
    },
    {
      title: formatMessage({ id: 'rental.index.table.header.rental_code' }),
      dataIndex: 'rental_code',
      width: 70
    },
    {
      title: formatMessage({ id: 'rental.index.table.header.cost' }),
      dataIndex: 'displayPrice',
      align: 'right',
      width: 100,
      render: (text) => priceFormatter(text)
    },
    {
      title: formatMessage({ id: 'rental.index.table.header.actions' }),
      key: 'operation',
      fixed: 'right',
      className: 'td-action',
      width: 240,
      render: (_, rental) => <TableAction rental={rental} />
    }
  ]

  return (
    <KTable
      params={params}
      recordType={RecordTypeEnum.rental}
      columns={columns}
      renderMobileRow={(record: IRental) => <Item member={record} />}
      empty={{
        emptySearchText: tCore({ id: 'empty_search_text' }),
        default: <RentalEmpty props={{ hasRentals, setFilter }} />
      }}
      {...others}
    />
  )
}

export default RentalTable
